/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @stylistic/max-len */
import React, { useCallback, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPalette } from "@fortawesome/free-solid-svg-icons";
import { debounce } from "lodash";
import Button from "../../../../Parts/Button";
import PowerShadesTypeahead from "../../../../Parts/PowerShadesTypeahead";
import { IndoorShadeOptionsDict, IndoorManualChain, IndoorMotorType, IndoorShadeOptionsSingleDict, IndoorShadeConfiguration, IndoorEndCaps, IndoorHeaderType, IndoorMountType, PortalShadeOption, IndoorDriveType, PortalShadeOptionItem, IndoorShadeOptionsCoupledDict } from "../../../../Store/entities/assemblies/types";
import FractionField from "../../../../Quote/NewOptionFields/FractionField";
import CoupledShadeTable from "../../../../Quote/NewItems/CoupledShadeTable";
import { ErrorSet, FullErrorWarningSet } from "../../../../Store/entities/assemblies/errors/hooks";
import ColorPaletteModal from "../../../ColorPaletteModal/ColorPaletteModal";
import ColorOptionsDropdown from "../../../ColorPaletteModal/ColorOptionsDropdown";
import { onlyActive } from "../../../../psUtil";
import { OptionFooter, OptionLabel } from "../../../../Parts/FormParts";
import FabricSelect from "../../../../Quote/NewOptionFields/FabricSelect/FabricSelect";
import { FabricViewModel } from "../../../../powershadesApiTypes";

type MassEditIndoorInnerProps = {
	options: IndoorShadeOptionsDict;
	quoteType: string;
	fabrics: FabricViewModel[];
	isNotAdd?: boolean;
	massEdit?: boolean | undefined;
	unlocked?: boolean;
	errorWarningSet?: FullErrorWarningSet;
	pickerState?: boolean;
	setPickerState?: (isOpen: boolean) => void;
	showBands?: boolean;
	onBandOverrideSelect?: () => void;
	coupledOptions: IndoorShadeOptionsCoupledDict;
	allowBandOverride?: boolean;
	onShadeConfigurationEdit?: (val?: IndoorShadeConfiguration) => void;
};

const MassEditIndoorInner = ({
	options,
	quoteType,
	isNotAdd,
	massEdit,
	unlocked = true,
	errorWarningSet,
	pickerState,
	setPickerState,
	fabrics,
	showBands = false,
	onBandOverrideSelect,
	coupledOptions,
	allowBandOverride = false,
	onShadeConfigurationEdit,
}: MassEditIndoorInnerProps) => {
	// Extract shade option objects from props
	const {
		Height,
		HeaderMountType,
		ControlSide,
		HeaderType,
		RoomName,
		ShadeName,
		DirectionFacing,
		Floor,
		ShadeConfiguration,
		DriveType,
		TotalWidth,
		MotorType,
		ManualType,
		EndCaps,
		SideChannels,
		HembarLine,
		HembarStyle,
		WindowJambDepth,
		LVPowerSupply,
		BalancedLightGaps,
		ColorPalettes,
		CustomFabricWidth,
		FabricAlignment,
		Colors,
	} = options;

	// Config and option  flags
	const isMotorized = DriveType.getValue() === "motorized";
	const shadeConfigValue = ShadeConfiguration.getValue();
	const isDualLike = shadeConfigValue === "dual" || shadeConfigValue === "dual_coupled";
	const isCoupled = shadeConfigValue === "coupled" || shadeConfigValue === "dual_coupled";
	const innerRollDirectionOption = isDualLike ? (options as any).FrontRollDirection : (options as IndoorShadeOptionsSingleDict).RollDirection;
	const outerRollDirectionOption = (options as any).BackRollDirection;
	const innerFabric = isDualLike ? (options as any).FrontFabric : (options as IndoorShadeOptionsSingleDict).Fabric;
	const outerFabric = (options as any).BackFabric;
	const fasciaSplit = coupledOptions.FasciaSplit;
	// Only render Fascia Split when in a coupled configuration and there are active options available across selection
	const hasActiveFasciaSplitOptions = !!fasciaSplit && Array.isArray(fasciaSplit.options) && (onlyActive(fasciaSplit.options).length > 0);
	const bandCountOptions = [
		<option key={0} value={0}>-</option>,
		...coupledOptions.BandCount.options?.map((option) => <option key={option.value} value={option.value}>{option.label}</option>) ?? [],
	];

	// Error data and helpers
	const errors: ErrorSet[] = errorWarningSet?.errors ?? [];
	const erroredClass = "border border-danger";
	const errorClass = (option: PortalShadeOption<any>) => (errors.find((error) => (!massEdit && (error.varName === option?.varName))) ? erroredClass : "");
	const disabled = !unlocked;

	// Local UI state
	const [emphasizing, setEmphasizing] = useState(false);
	const [_groupClass, setGroupClass] = useState("px-2 rounded");
	const [optionClass, setOptionClass] = useState("");
	const [driveTypeSelected, setDriveTypeSelected] = useState(false);
	const [shadeConfigState, setShadeConfigState] = useState<string | PortalShadeOptionItem<IndoorShadeConfiguration>>(ShadeConfiguration.getSelectedOption());
	const [sideChannelState, setSideChannelState] = useState<string>(SideChannels.getSelectedOption().value);

	// Color palette picker state
	const isColorPickerOpen = pickerState ?? false;
	const setIsColorPickerOpen = setPickerState ?? (() => console.log("Failed to open color picker."));
	const [colorPaletteState, setColorPaletteState] = useState<string | PortalShadeOptionItem<string>>(ColorPalettes.getValue());
	const [colorBtnDisabled, setColorBtnDisabled] = useState(colorPaletteState === "");
	const activeColorPalettes = onlyActive(ColorPalettes.options?.filter((palette) => palette.value !== "Custom Palette"));
	const toggleColorPicker = () => {
		colorPaletteState === "" && setColorPaletteState("Custom Palette");
		setIsColorPickerOpen(!isColorPickerOpen);
	};

	const currentPaletteValue = ColorPalettes.getValue();
	React.useEffect(() => {
		setColorPaletteState(currentPaletteValue);
		setColorBtnDisabled(currentPaletteValue === "");
	}, [currentPaletteValue]);

	const selectedPaletteForTypeahead = (() => {
		if (typeof colorPaletteState === "object" && colorPaletteState) {
			return [colorPaletteState];
		}
		if (typeof colorPaletteState === "string" && colorPaletteState !== "") {
			const paletteMatch = (ColorPalettes?.options ?? []).find((opt) => opt.value === colorPaletteState);
			if (paletteMatch) return [paletteMatch];
			return [{
				value: colorPaletteState,
				label: colorPaletteState,
				title: colorPaletteState,
			} as PortalShadeOptionItem<string>];
		}
		return [];
	})();

	// Debounced setters and state for text fields that need it
	const [roomNameValue, setRoomNameValue] = useState(RoomName.getValue());
	const [shadeNameValue, setShadeNameValue] = useState(ShadeName.getValue());
	const debouncedSetRoomName = useCallback(
		debounce((value) => RoomName.setValue(value), 500),
		[options]
	);
	const debouncedSetShadeName = useCallback(
		debounce((value) => ShadeName.setValue(value), 500),
		[options]
	);

	// End caps color dropdown logic
	const endCapRegex = /^(?=.*end cap)(?!.*hembar).*$/i;
	const endCapColorOptions = (Colors ?? []).filter((colorOption) =>
		!!colorOption.colorShow && endCapRegex.test(colorOption.label.toLowerCase()));
	const selectedColorPaletteOption = ColorPalettes.getSelectedOption();
	const paletteDefaultColors = selectedColorPaletteOption?.default_colors ?? {};
	const selectedEndCapsDropdownOption = React.useMemo<PortalShadeOptionItem<string> | undefined>(() => {
		const capOpt = endCapColorOptions?.[0] as PortalShadeOption<string> | undefined;
		if (!capOpt) return undefined;
		const explicitVal = (capOpt as any).getValue ? ((capOpt as any).getValue() as string | undefined) : undefined;
		const opts = (capOpt.options ?? []) as PortalShadeOptionItem<string>[];
		if (explicitVal && explicitVal !== "") {
			const byVal = opts.find((o) => o?.value === explicitVal || o?.label === explicitVal);
			if (byVal) return byVal;
		}
		const current = capOpt.getSelectedOption ? (capOpt.getSelectedOption() as unknown as PortalShadeOptionItem<string> | undefined) : undefined;
		const hasValid = !!current && typeof current.value === "string" && current.value !== "" && current.value !== "Missing Default Color";
		if (hasValid) return current as PortalShadeOptionItem<string>;
		const defaultVal = (paletteDefaultColors?.[capOpt.label] as string | undefined) ?? undefined;
		if (!defaultVal) return undefined;
		const match = opts.find((o) => o?.value === defaultVal || o?.label === defaultVal) as PortalShadeOptionItem<string> | undefined;
		return match;
	}, [endCapColorOptions, paletteDefaultColors]);

	// Emphasis animation logic
	const emphasizeOptions = () => {
		if (!isNotAdd && !driveTypeSelected) return setDriveTypeSelected(true);
		const initialOption = "rounded";
		const initialGroup = "px-2 rounded";
		const optionEmphasis = "rounded border-warning";
		const groupEmphasis = "px-2 rounded border-warning bg-warning-subtle";
		setEmphasizing(true);
		setGroupClass(groupEmphasis);
		setOptionClass(optionEmphasis);
		setTimeout(() => {
			setGroupClass(initialGroup);
			setOptionClass(initialOption);
		}, 400);
		setTimeout(() => {
			setGroupClass(groupEmphasis);
			setOptionClass(optionEmphasis);
		}, 800);
		setTimeout(() => {
			setGroupClass(initialGroup);
			setOptionClass(initialOption);
		}, 1200);
		setTimeout(() => {
			setGroupClass(groupEmphasis);
			setOptionClass(optionEmphasis);
		}, 1600);
		setTimeout(() => {
			setGroupClass(initialGroup);
			setOptionClass(initialOption);
		}, 2000);
		setTimeout(() => {
			setGroupClass(groupEmphasis);
			setOptionClass(optionEmphasis);
			setEmphasizing(false);
		}, 2400);
	};

	// Highlight fields with shared values when mass editing
	const isSharedValue = (property: PortalShadeOption<any>) => massEdit && property.getValue() && "border border-2 border-primary rounded";

	return isColorPickerOpen ? (
		<ColorPaletteModal
			colors={Colors}
			colorPalettes={{ ...ColorPalettes, options: activeColorPalettes }}
			errorClass={errorClass}
			quoteType={quoteType}
			highlightSharedValues={!!massEdit}
		/>
	) : (
		<div className="d-flex flex-row flex-wrap">
			<div className="p-2 w-50">
				{OptionLabel(DriveType)}
				<PowerShadesTypeahead
					disabled={disabled || onlyActive(DriveType.options).length === 0}
					id="header-mount-type"
					options={onlyActive(DriveType.options) ?? []}
					onChange={(selected) => {
						if (selected.length === 0) {
							DriveType.clearValue();
							return;
						}
						const selectedObj = selected[0] as {
							value: IndoorDriveType;
						};

						emphasizeOptions();
						DriveType.setValueAndClearMotorOptions(selectedObj.value);
					}}
					selected={[DriveType.getSelectedOption()]}
					inputClass={isSharedValue(DriveType)}
				/>
				<OptionFooter option={DriveType} quoteType={quoteType} />
			</div>
			<div className="p-2 w-50">
				{OptionLabel(HeaderType)}
				<PowerShadesTypeahead
					disabled={disabled || onlyActive(HeaderType.options).length === 0}
					id="header-type"
					options={onlyActive(HeaderType.options)}
					onChange={(selected) => {
						ColorPalettes.clearValue();
						setColorPaletteState("");
						setColorBtnDisabled(true);
						if (selected.length === 0) {
							HeaderType.clearValue();
							return;
						}
						const selectedObj = selected[0] as {
							value: IndoorHeaderType;
						};
						HeaderType.setValue(selectedObj.value);
					}}
					selected={[HeaderType.getSelectedOption()]}
					errored={errorClass(HeaderType) !== ""}
					inputClass={isSharedValue(HeaderType)}
				/>
				<OptionFooter option={HeaderType} quoteType={quoteType} />
			</div>
			{((!massEdit && isMotorized) || (isSharedValue(DriveType) && isMotorized)) ? null : (
				<div className="p-2 w-50">
					{OptionLabel(ManualType)}
					<PowerShadesTypeahead
						className={`${ManualType.getSelectedOption() ? "" : optionClass}`}
						style={{ border: "solid transparent 2px", transition: "all .1s" }}
						disabled={disabled || onlyActive(ManualType.options).length === 0}
						id="drive-type"
						options={onlyActive(ManualType.options) ?? []}
						onChange={(selected) => {
							if (selected.length === 0) {
								ManualType.clearValue();
								return;
							}
							const selectedObj = selected[0] as {
								value: IndoorManualChain;
							};

							ManualType.setValue(selectedObj.value as never);
						}}
						selected={[ManualType.getSelectedOption()]}
						errored={!emphasizing && errorClass(ManualType) !== ""}
						inputClass={isSharedValue(ManualType)}
					/>
					<OptionFooter option={ManualType} quoteType={quoteType} />
				</div>
			)}
			{((!massEdit && !isMotorized) || (isSharedValue(DriveType) && !isMotorized)) ? null : (
				<div className="p-2 w-50">
					{OptionLabel(MotorType)}
					<PowerShadesTypeahead
						className={`${MotorType.getSelectedOption() ? "" : optionClass}`}
						style={{ border: "solid transparent 2px", transition: "all .1s" }}
						disabled={disabled || onlyActive(MotorType.options).length === 0}
						id="drive-type"
						options={onlyActive(MotorType.options) ?? []}
						onChange={(selected) => {
							if (selected.length === 0) {
								MotorType.clearValue();
								return;
							}
							const selectedObj = selected[0] as {
								value: IndoorMotorType;
							};

							MotorType.setValue(selectedObj.value as never);
						}}
						selected={[MotorType.getSelectedOption()]}
						errored={!emphasizing && errorClass(MotorType) !== ""}
						inputClass={isSharedValue(MotorType)}
					/>
					<OptionFooter option={MotorType} quoteType={quoteType} />
				</div>
			)}
			<div className="p-2 w-50">
				{OptionLabel(ShadeConfiguration)}
				<PowerShadesTypeahead
					disabled={disabled || onlyActive(ShadeConfiguration.options).length === 0}
					id="shade-configuration"
					options={onlyActive(ShadeConfiguration.options) ?? []}
					onChange={(selected) => {
						if (selected.length === 0) {
							ShadeConfiguration.clearValue();
							setShadeConfigState("");
							if (onShadeConfigurationEdit) onShadeConfigurationEdit(undefined);
							return;
						}
						const selectedObj = selected[0] as {
							value: IndoorShadeConfiguration;
							label: string;
						};

						ShadeConfiguration.setValue(selectedObj.value);
						setShadeConfigState(selectedObj.label);
						if (onShadeConfigurationEdit) onShadeConfigurationEdit(selectedObj.value);
					}}
					selected={[shadeConfigState]}
					errored={errorClass(ShadeConfiguration) !== ""}
					inputClass={isSharedValue(ShadeConfiguration)}
				/>
				<OptionFooter option={ShadeConfiguration} quoteType={quoteType} />
			</div>
			<div className="p-2 w-50">
				{OptionLabel(HeaderMountType)}
				<OptionFooter
					option={HeaderMountType}
					quoteType={quoteType}
					optionField={
						<PowerShadesTypeahead
							disabled={disabled || onlyActive(HeaderMountType.options).length === 0}
							id="header-mount-type"
							options={onlyActive(HeaderMountType.options)}
							onChange={(selected) => {
								if (selected.length === 0) {
									HeaderMountType.clearValue();
									return;
								}
								const selectedObj = selected[0] as {
									value: IndoorMountType;
								};

								HeaderMountType.setValue(selectedObj.value);
							}}
							selected={[HeaderMountType.getSelectedOption()]}
							errored={errorClass(HeaderMountType) !== ""}
							inputClass={isSharedValue(HeaderMountType)}
						/>
					}
				/>
			</div>
			<div className="w-100 p-0 d-flex flex-row">
				<div className="p-2 w-50">
					{OptionLabel(TotalWidth)}
					<OptionFooter
						option={TotalWidth}
						quoteType={quoteType}
						optionField={
							<FractionField
								extraClasses={` ${errorClass(TotalWidth)} ${isSharedValue(TotalWidth)}`}
								key="total-width-edit"
								disabled={disabled}
								onBlur={(value) => TotalWidth.setValue(value)}
								value={TotalWidth.getValue()}
								minValue={TotalWidth.minValue ?? 0}
								maxValue={TotalWidth.maxValue ?? 0}
								varName="total_width"
							/>
						}
					/>
				</div>
				<div className="p-2 w-50">
					{OptionLabel(Height)}
					<OptionFooter
						option={Height}
						quoteType={quoteType}
						optionField={
							<FractionField
								extraClasses={` ${errorClass(Height)} ${isSharedValue(Height)}`}
								key="height-edit"
								disabled={disabled}
								onBlur={(value) => Height.setValue(value)}
								value={Height.getValue()}
								minValue={Height.minValue ?? 0}
								maxValue={Height.maxValue ?? 0}
								varName="height"
							/>
						}
					/>
				</div>
			</div>
			{isCoupled
				&& showBands && (
				<div className="p-2 w-100">
					<CoupledShadeTable
						widthsOption={coupledOptions.CoupledWidth}
						bandCountOption={coupledOptions.BandCount}
						disabled={disabled}
					/>
				</div>
			)}
			{isCoupled && !showBands && allowBandOverride && (
				<label className="p-2 w-100 d-flex flex-column align-items-center">
					Override Band Count to Edit Widths
					<select
						className="p-2 w-25 mt-2"
						value="-"
						disabled={disabled}
						onChange={(e) => {
							onBandOverrideSelect?.();
							coupledOptions.BandCount.setValue(Number(e.target.value));
						}}
					>
						{bandCountOptions}
					</select>
				</label>
			)}
			{(CustomFabricWidth.active && FabricAlignment.active) && (
				<>
					<div className="p-2 w-50">
						{OptionLabel(CustomFabricWidth)}
						<FractionField
							extraClasses={` ${errorClass(CustomFabricWidth)} ${isSharedValue(CustomFabricWidth)}`}
							key="total-width-edit"
							disabled={disabled}
							onBlur={(value) => CustomFabricWidth.setValue(value)}
							value={CustomFabricWidth.getValue()}
							minValue={CustomFabricWidth.minValue ?? 0}
							maxValue={CustomFabricWidth.maxValue ?? 0}
							varName="custom_fabric_width"
						/>
						<OptionFooter option={TotalWidth} quoteType={quoteType} />
					</div>
					<div className="p-2 w-50">
						{OptionLabel(FabricAlignment)}
						<PowerShadesTypeahead
							disabled={disabled || onlyActive(FabricAlignment.options).length === 0}
							id="header-mount-type"
							options={onlyActive(FabricAlignment.options)}
							onChange={(selected) => {
								if (selected.length === 0) {
									FabricAlignment.clearValue();
									return;
								}
								const selectedObj = selected[0] as {
									value: string;
								};

								FabricAlignment.setValue(selectedObj.value);
							}}
							selected={[FabricAlignment.getSelectedOption()]}
							errored={errorClass(FabricAlignment) !== ""}
							inputClass={isSharedValue(FabricAlignment)}
						/>
						<OptionFooter option={FabricAlignment} quoteType={quoteType} />
					</div>
				</>
			)}
			<div className="p-2 w-100">
				{OptionLabel(innerFabric)}
				<FabricSelect
					value={innerFabric.getValue() ?? ""}
					fabrics={fabrics}
					options={innerFabric.options}
					disabled={disabled || onlyActive(innerFabric.options).length === 0}
					onChange={(newFabricName) => {
						newFabricName
							? innerFabric.setValue(newFabricName)
							: innerFabric.clearValue();
					}}
					// @ts-ignore - Added for compilation.
					styles={{ control: (base) => ({ ...base, border: isSharedValue(innerFabric) ? "2px solid #8dc63f" : (base.border) }) }}
				/>
				<OptionFooter option={innerFabric} quoteType={quoteType} />
			</div>
			{isDualLike && (
				<div className="p-2 w-50">
					{OptionLabel(outerFabric)}
					<FabricSelect
						disabled={disabled || onlyActive(outerFabric.options).length === 0}
						value={outerFabric.getValue() ?? ""}
						options={outerFabric.options}
						onChange={(newFabricName) => {
							newFabricName
								? outerFabric.setValue(newFabricName)
								: outerFabric.clearValue();
						}}
						fabrics={fabrics}
						// @ts-ignore - Added for TS compilation.
						styles={{ control: (base) => ({ ...base, border: isSharedValue(outerFabric) ? "2px solid #8dc63f" : base.border }) }}
					/>
					<OptionFooter option={outerFabric} quoteType={quoteType} />
				</div>
			)}
			{(isCoupled && fasciaSplit && fasciaSplit.active && hasActiveFasciaSplitOptions) && (
				<div className="p-2 w-50">
					{OptionLabel(fasciaSplit)}
					<PowerShadesTypeahead
						disabled={disabled || !hasActiveFasciaSplitOptions}
						id="fascia-split"
						options={hasActiveFasciaSplitOptions ? onlyActive(fasciaSplit.options) : []}
						onChange={(selected) => {
							setGroupClass("px-2 rounded");
							if (selected.length === 0) {
								fasciaSplit.clearValue();
								return;
							}
							const selectedObj = selected[0] as {
								value;
							};
							fasciaSplit.setValue(selectedObj.value);
						}}
						selected={[fasciaSplit.getSelectedOption()]}
						errored={errorClass(fasciaSplit) !== ""}
						inputClass={isSharedValue(fasciaSplit)}
					/>
					<OptionFooter option={fasciaSplit} quoteType={quoteType} />
				</div>
			)}
			<div className="p-2 w-50">
				{OptionLabel(ControlSide)}
				<PowerShadesTypeahead
					disabled={disabled || onlyActive(ControlSide.options).length === 0}
					id="control-side"
					options={onlyActive(ControlSide.options) ?? []}
					onChange={(selected) => {
						setGroupClass("px-2 rounded");
						if (selected.length === 0) {
							ControlSide.clearValue();
							return;
						}
						const selectedObj = selected[0] as {
							value;
						};
						ControlSide.setValue(selectedObj.value);
					}}
					selected={[ControlSide.getSelectedOption()]}
					errored={errorClass(ControlSide) !== ""}
					inputClass={isSharedValue(ControlSide)}
				/>
				<OptionFooter option={ControlSide} quoteType={quoteType} />
			</div>
			{!isDualLike && (
				<div className="p-2 w-50">
					{OptionLabel(innerRollDirectionOption)}
					<PowerShadesTypeahead
						disabled={disabled || onlyActive(innerRollDirectionOption.options).length === 0}
						id="inner-roll-direction"
						options={onlyActive(innerRollDirectionOption.options) ?? []}
						onChange={(selected) => {
							if (selected.length === 0) {
								innerRollDirectionOption.clearValue();
								return;
							}
							const selectedObj = selected[0] as {
								value;
							};

							innerRollDirectionOption.setValue(selectedObj.value);
						}}
						selected={[innerRollDirectionOption.getSelectedOption()]}
						clearButton={innerRollDirectionOption.getSelectedOption().label !== ""}
						errored={errorClass(innerRollDirectionOption) !== ""}
						inputClass={isSharedValue(innerRollDirectionOption)}
					/>
					<OptionFooter option={innerRollDirectionOption} quoteType={quoteType} />
				</div>
			)}
			{isDualLike && (
				<>
					<div className="p-2 w-50">
						{OptionLabel(innerRollDirectionOption)}
						<PowerShadesTypeahead
							disabled={disabled || onlyActive(innerRollDirectionOption.options).length === 0}
							id="inner-roll-direction"
							options={onlyActive(innerRollDirectionOption.options) ?? []}
							onChange={(selected) => {
								if (selected.length === 0) {
									innerRollDirectionOption.clearValue();
									return;
								}
								const selectedObj = selected[0] as {
									value;
								};

								innerRollDirectionOption.setValue(selectedObj.value);
							}}
							selected={[innerRollDirectionOption.getSelectedOption()]}
							clearButton={innerRollDirectionOption.getSelectedOption().label !== ""}
							errored={errorClass(innerRollDirectionOption) !== ""}
							inputClass={isSharedValue(innerRollDirectionOption)}
						/>
						<OptionFooter option={innerRollDirectionOption} quoteType={quoteType} />
					</div>
					<div className="p-2 w-50">
						{OptionLabel(outerRollDirectionOption)}
						<PowerShadesTypeahead
							disabled={disabled || onlyActive(outerRollDirectionOption.options).length === 0}
							id="outer-roll-direction"
							options={onlyActive(outerRollDirectionOption.options)}
							onChange={(selected) => {
								if (selected.length === 0) {
									outerRollDirectionOption.clearValue();
									return;
								}
								const selectedObj = selected[0] as {
									value;
								};

								outerRollDirectionOption.setValue(selectedObj.value);
							}}
							selected={[outerRollDirectionOption.getSelectedOption()]}
							clearButton={outerRollDirectionOption.getSelectedOption().label !== ""}
							errored={errorClass(outerRollDirectionOption) !== ""}
							inputClass={isSharedValue(outerRollDirectionOption)}
						/>
						<OptionFooter option={outerRollDirectionOption} quoteType={quoteType} />
					</div>
				</>
			)}
			<div className="p-2 w-50">
				{OptionLabel(HembarLine)}
				<PowerShadesTypeahead
					disabled={disabled || onlyActive(HembarLine.options).length === 0}
					id="hembar-type"
					options={onlyActive(HembarLine.options) ?? []}
					onChange={(selected) => {
						setGroupClass("px-2 rounded");
						if (selected.length === 0) {
							HembarLine.clearValue();
							return;
						}
						const selectedObj = selected[0] as {
							value;
						};
						HembarLine.setValue(selectedObj.value);
					}}
					selected={[HembarLine.getSelectedOption()]}
					errored={errorClass(HembarLine) !== ""}
					inputClass={isSharedValue(HembarLine)}
				/>
				<OptionFooter option={HembarLine} quoteType={quoteType} />
			</div>
			<div className="p-2 w-50">
				{OptionLabel(HembarStyle)}
				<PowerShadesTypeahead
					disabled={disabled || onlyActive(HembarStyle.options).length === 0}
					id="hembar-type"
					options={onlyActive(HembarStyle.options) ?? []}
					onChange={(selected) => {
						setGroupClass("px-2 rounded");
						if (selected.length === 0) {
							HembarStyle.clearValue();
							return;
						}
						const selectedObj = selected[0] as {
							value;
						};
						HembarStyle.setValue(selectedObj.value);
					}}
					selected={[HembarStyle.getSelectedOption()]}
					errored={errorClass(HembarStyle) !== ""}
					inputClass={isSharedValue(HembarStyle)}
				/>
				<OptionFooter option={HembarStyle} quoteType={quoteType} />
			</div>
			{SideChannels.active && (
				<div className="p-2 w-50">
					{OptionLabel(SideChannels)}
					<PowerShadesTypeahead
						disabled={disabled || onlyActive(SideChannels.options).length === 0}
						id="side-channels"
						options={onlyActive(SideChannels.options) ?? []}
						onChange={(selected) => {
							setGroupClass("px-2 rounded");
							if (selected.length === 0) {
								SideChannels.clearValue();
								setSideChannelState("");
								return;
							}
							const selectedObj = selected[0] as {
								value;
								label;
							};
							SideChannels.setValue(selectedObj.value);
							setSideChannelState(selectedObj.label);
						}}
						selected={[sideChannelState]}
						errored={errorClass(SideChannels) !== ""}
						inputClass={isSharedValue(SideChannels)}
					/>
					<OptionFooter option={SideChannels} quoteType={quoteType} />
				</div>
			)}
			{LVPowerSupply.active && (
				<div className="p-2 w-50">
					{OptionLabel(LVPowerSupply)}
					<PowerShadesTypeahead
						disabled={disabled || onlyActive(LVPowerSupply.options).length === 0}
						id="lv-power-supply"
						options={onlyActive(LVPowerSupply.options) ?? []}
						onChange={(selected) => {
							setGroupClass("px-2 rounded");
							if (selected.length === 0) {
								LVPowerSupply.clearValue();
								return;
							}
							const selectedObj = selected[0] as {
								value;
							};
							LVPowerSupply.setValue(selectedObj.value);
						}}
						selected={[LVPowerSupply.getSelectedOption()]}
						className="text-center"
						errored={errorClass(LVPowerSupply) !== ""}
						inputClass={isSharedValue(LVPowerSupply)}
					/>
					<OptionFooter option={LVPowerSupply} quoteType={quoteType} />
				</div>
			)}
			{BalancedLightGaps.active && (
				<div className="p-2 w-50">
					{OptionLabel(BalancedLightGaps)}
					<PowerShadesTypeahead
						disabled={disabled || onlyActive(BalancedLightGaps.options).length === 0}
						id="balanced-lightgaps"
						options={onlyActive(BalancedLightGaps.options) ?? []}
						onChange={(selected) => {
							setGroupClass("px-2 rounded");
							if (selected.length === 0) {
								BalancedLightGaps.clearValue();
								return;
							}
							const selectedObj = selected[0] as {
								value;
							};
							BalancedLightGaps.setValue(selectedObj.value);
						}}
						selected={[BalancedLightGaps.getSelectedOption()]}
						className="text-center"
						errored={errorClass(BalancedLightGaps) !== ""}
						inputClass={isSharedValue(BalancedLightGaps)}
					/>
					<OptionFooter option={BalancedLightGaps} quoteType={quoteType} />
				</div>
			)}
			{WindowJambDepth.active && (
				<div className="p-2 w-50">
					{OptionLabel(WindowJambDepth)}
					<FractionField
						key="window_jamb_depth"
						disabled={disabled}
						onBlur={(value) => {
							setGroupClass("px-2 rounded");
							WindowJambDepth.setValue(value);
						}}
						value={WindowJambDepth.getValue()}
						minValue={WindowJambDepth.minValue ?? 0}
						maxValue={WindowJambDepth.maxValue ?? 0}
						varName="window_jamb_depth"
						extraClasses={` ${errorClass(WindowJambDepth)} ${isSharedValue(WindowJambDepth)}`}
					/>
					<OptionFooter option={WindowJambDepth} quoteType={quoteType} />
				</div>
			)}
			<div className="p-2 w-100">
				{OptionLabel(ColorPalettes)}
				<div className="d-flex flex-col justify-content-between flex-grow-1">
					<PowerShadesTypeahead
						disabled={disabled || !ColorPalettes?.options}
						id="color-palettes"
						options={activeColorPalettes}
						onChange={(selected) => {
							setColorPaletteState("");
							if (selected.length === 0) {
								ColorPalettes.clearValue();
								setColorPaletteState("");
								setColorBtnDisabled(true);
								return;
							}
							setColorBtnDisabled(false);
							const selectedObj = selected[0] as {
								value;
							};
							ColorPalettes.setValue(selectedObj.value);
							setColorPaletteState(selectedObj.value);
						}}
						selected={selectedPaletteForTypeahead}
						errored={errorClass(ColorPalettes) !== ""}
						inputClass={isSharedValue(ColorPalettes)}
					/>
					<Button
						color="green"
						disabled={disabled || !ColorPalettes?.options || onlyActive(ColorPalettes.options).length <= 1 || (colorBtnDisabled && !massEdit)}
						onClick={toggleColorPicker}
						style={{ minWidth: "40px", borderRadius: "6px", height: "38px", marginLeft: "5px" }}
					>
						<FontAwesomeIcon icon={faPalette} />
					</Button>
				</div>
				<OptionFooter option={ColorPalettes} quoteType={quoteType} />
			</div>
			{
				EndCaps?.options && EndCaps.options?.length ? <div className="p-2 w-50">
					{OptionLabel(EndCaps)}
					<PowerShadesTypeahead
						disabled={disabled || onlyActive(EndCaps.options).length === 0}
						id="endcaps"
						options={onlyActive(EndCaps.options) ?? []}
						onChange={(selected) => {
							if (selected.length === 0) {
								EndCaps.clearValue();
								return;
							}
							const selectedEndCaps = selected[0] as {
								value: IndoorEndCaps;
							};
							EndCaps.setValue(selectedEndCaps.value);
						}}
						selected={[EndCaps.getSelectedOption()]}
						errored={errorClass(EndCaps) !== ""}
						inputClass={isSharedValue(EndCaps)}
					/>
					<OptionFooter option={EndCaps} quoteType={quoteType} />
				</div>
					: null
			}
			{
				(EndCaps?.getSelectedOption()?.value && endCapColorOptions?.length > 0 && !!endCapColorOptions[0])
				&& <div key="End Caps Color" className="p-2 w-50">
					<ColorOptionsDropdown
						title="End Caps Color"
						defaultColor={paletteDefaultColors?.[endCapColorOptions[0]?.label]}
						options={endCapColorOptions[0]?.options ?? []}
						onSelect={(value) => endCapColorOptions[0]?.setValue(value)}
						colorPalettes={ColorPalettes}
						selectedOption={selectedEndCapsDropdownOption}
						disabled={disabled}
						className={isSharedValue(endCapColorOptions[0])}
					/>
				</div>

			}
			<div className={`d-flex${DirectionFacing.active ? " w-100" : ""}`}>
				{DirectionFacing.active && (
					<div className="p-2 w-50">
						{OptionLabel(DirectionFacing)}
						<PowerShadesTypeahead
							id="direction-facing"
							clearButton={!!DirectionFacing.getSelectedOption()}
							options={onlyActive(DirectionFacing.options) ?? []}
							onChange={(selected) => {
								if (selected.length === 0) {
									DirectionFacing.clearValue();
									return;
								}
								const selectedObj = selected[0] as {
									value;
								};

								DirectionFacing.setValue(selectedObj.value);
							}}
							selected={[DirectionFacing.getSelectedOption()]}
							errored={errorClass(DirectionFacing) !== ""}
							inputClass={isSharedValue(DirectionFacing)}
						/>
						<OptionFooter option={DirectionFacing} quoteType={quoteType} />
					</div>
				)}
				<div className="p-2 w-50">
					{OptionLabel(Floor)}
					<input
						type="number"
						className={`rounded input-group p-2 ${errorClass(Floor)} ${isSharedValue(Floor)}`}
						value={Floor.getValue()}
						onChange={(e) => Floor.setValue(Number(e.target.value))}
					/>
					<OptionFooter option={Floor} quoteType={quoteType} />
				</div>
			</div>

			<div className="w-100 d-flex">
				<div className="p-2 w-50">
					{OptionLabel(RoomName)}
					<input
						className={`rounded input-group p-2 ${errorClass(RoomName)} ${isSharedValue(RoomName)}`}
						type="text"
						value={roomNameValue}
						onChange={(e) => {
							setRoomNameValue(e.target.value);
							debouncedSetRoomName(e.target.value);
						}}
					/>
					<OptionFooter option={RoomName} quoteType={quoteType} />
				</div>
				<div className="p-2 w-50">
					{OptionLabel(ShadeName)}
					<input
						className={`rounded input-group p-2 ${errorClass(ShadeName)} ${isSharedValue(ShadeName)}`}
						disabled={disabled}
						type="text"
						value={shadeNameValue}
						onChange={(e) => {
							setShadeNameValue(e.target.value);
							debouncedSetShadeName(e.target.value);
						}}
					/>
					<OptionFooter option={ShadeName} quoteType={quoteType} />
				</div>
			</div>
		</div>
	);
};

export default MassEditIndoorInner;
